import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { ThemeEvents, ProductSummaryUpdateEvent, ProductDeliveryUpdateEvent } from '@theme/events';

const ESTIMATE_PATH = '/apps/location-delivery';
const ESTIMATE_CACHE_PREFIX = 'product-delivery:';
const ESTIMATE_CACHE_LIFETIME = 60 * 60 * 1000;
const ESTIMATE_TIMEOUT = 8000;
const TRANSIT_DAYS = 3;
/** State and territory codes whose delivery is not free */
const REMOTE_REGIONS = ['AK', 'HI', 'GU', 'PR', 'VI', 'AS', 'MP'];
/** @type {Intl.DateTimeFormatOptions} */
const DATE_FORMAT = { weekday: 'short', month: 'long', day: 'numeric' };

/**
 * @typedef {Object} DeliveryEstimate
 * @property {string} date - The delivery date as YYYY-MM-DD
 * @property {string} regionCode - The destination's state or territory code
 */

/** @type {Map<string, Promise<DeliveryEstimate | null>>} */
const estimateRequests = new Map();

/**
 * Asks the delivery estimator once per page, reusing its answer for the session.
 * @param {string} url - The estimator URL.
 * @returns {Promise<DeliveryEstimate | null>} Null when the estimator has no delivery to the destination.
 */
function requestEstimate(url) {
  let request = estimateRequests.get(url);

  if (!request) {
    request = fetchEstimate(url);
    estimateRequests.set(url, request);
  }

  return request;
}

/**
 * @param {string} url - The estimator URL.
 * @returns {Promise<DeliveryEstimate | null>}
 */
async function fetchEstimate(url) {
  const cacheKey = ESTIMATE_CACHE_PREFIX + url;
  let data = readCachedEstimate(cacheKey);

  if (!data) {
    const response = await fetch(url, { signal: AbortSignal.timeout(ESTIMATE_TIMEOUT) });
    if (!response.ok) throw new Error(`Delivery estimator responded ${response.status}`);

    data = await response.json();
    if (!data?.success) throw new Error('Delivery estimator reported a failure');

    writeCachedEstimate(cacheKey, data);
  }

  const date = data.delivery?.estimatedDate;
  return typeof date === 'string' ? { date, regionCode: data.location?.regionCode ?? '' } : null;
}

/**
 * @param {string} key - The storage key.
 * @returns {any} The stored response while it is fresh.
 */
function readCachedEstimate(key) {
  try {
    const entry = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    return entry && Date.now() - entry.time < ESTIMATE_CACHE_LIFETIME ? entry.data : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} key - The storage key.
 * @param {any} data - The estimator's response.
 */
function writeCachedEstimate(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), data }));
  } catch {
    // Storage may be full or blocked; the next page asks again
  }
}

/**
 * Parses a UTC offset such as `-0700` into minutes.
 * @param {string | undefined} offset - The offset.
 * @returns {number | null}
 */
function parseUtcOffset(offset) {
  const match = /^([+-])(\d{2})(\d{2})$/.exec(offset ?? '');
  if (!match) return null;

  const [, sign, hours, minutes] = match;
  return (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes));
}

/**
 * Keeps the delivery line on the selected variant and writes the text only the browser can resolve: the delivery
 * date and the dispatch day. The order cutoff is the warehouse's, so both are measured on the store's clock, and the
 * free delivery threshold is measured against the order summary's total.
 *
 * @extends {Component}
 */
class ProductDelivery extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | null} The order summary's total in minor units, once it has announced one. */
  #total = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    container?.addEventListener(ThemeEvents.productSummaryUpdate, this.#handleSummaryUpdate, { signal });

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLElement | null} */
  get #line() {
    return this.querySelector('.product-delivery__line');
  }

  /** @returns {HTMLElement | null} */
  get #text() {
    return this.querySelector('.product-delivery__text');
  }

  #render() {
    const mode = this.#line?.dataset.mode;

    if (mode === 'date') this.#renderDate();
    else if (mode === 'dispatch') this.#renderDispatch();
    else this.#announce();
  }

  /**
   * Tells the page what the line reads, so the sticky bar's copy of it stays in step.
   */
  #announce() {
    const text = this.#text;
    const parts = text?.querySelectorAll('.product-delivery__part');
    const contents = parts?.length ? Array.from(parts, (part) => part.textContent ?? '') : [text?.textContent ?? ''];

    this.dispatchEvent(
      new ProductDeliveryUpdateEvent(this.hidden ? [] : contents.map((content) => content.trim()).filter(Boolean))
    );
  }

  async #renderDate() {
    /** @type {DeliveryEstimate | null | undefined} */
    let estimate;

    if (this.hasAttribute('data-estimator')) {
      try {
        estimate = await requestEstimate(this.#estimateUrl);
      } catch (error) {
        console.warn('[product-delivery] Delivery estimator unavailable:', error);
      }
    }

    const text = this.#text;
    if (this.#line?.dataset.mode !== 'date' || !text) return;

    // The estimator knows the destination and has no delivery there
    if (estimate === null) {
      this.hidden = true;
      this.#announce();
      return;
    }

    const date = estimate ? this.#parseDate(estimate.date) : this.#calculateDeliveryDate();
    const regionCode = (this.dataset.province || estimate?.regionCode || '').toUpperCase();
    const template = REMOTE_REGIONS.includes(regionCode) ? this.dataset.deliversBy : this.dataset.freeDeliveryBy;
    const formattedDate = new Intl.DateTimeFormat(document.documentElement.lang || undefined, DATE_FORMAT).format(date);

    text.textContent = (template ?? '').replace('[date]', formattedDate);
    this.#announce();
  }

  #renderDispatch() {
    const text = this.#text;
    if (!text) return;

    const { date, hour } = this.#storeNow();
    const day = date.getDay();
    const cutoffHour = Number(this.dataset.cutoffHour);

    let dispatchDay = this.dataset.shipsToday;
    if (day === 0 || (day < 5 && hour >= cutoffHour)) dispatchDay = this.dataset.shipsTomorrow;
    if (day === 6 || (day === 5 && hour >= cutoffHour)) dispatchDay = this.dataset.shipsMonday;

    const threshold = Number(this.dataset.freeDeliveryThreshold) || 0;
    const total = this.#total ?? (Number(this.#line?.dataset.price) || 0);
    const freeDelivery = total >= threshold ? this.dataset.freeDelivery : this.dataset.freeDeliveryOver;

    text.replaceChildren(
      ...[dispatchDay, freeDelivery].map((content) => {
        const part = document.createElement('span');
        part.className = 'product-delivery__part';
        part.textContent = content ?? '';
        return part;
      })
    );
    this.#announce();
  }

  /** @returns {string} */
  get #estimateUrl() {
    // Transit time depends on the destination only, so one weight serves every product
    const parameters = new URLSearchParams({ weight: '5' });
    const { city, postalCode, province, country } = this.dataset;

    if (city && postalCode && province && country) {
      parameters.set('city', city);
      parameters.set('postalCode', postalCode);
      parameters.set('province', province);
      parameters.set('country', country);
    }

    return `${ESTIMATE_PATH}?${parameters}`;
  }

  /**
   * Counts the transit days from the day the order dispatches: the next day after the cutoff, and Monday rather
   * than Sunday.
   * @returns {Date}
   */
  #calculateDeliveryDate() {
    const { date, hour } = this.#storeNow();

    if (hour >= Number(this.dataset.cutoffHour)) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    }

    date.setDate(date.getDate() + TRANSIT_DAYS);
    return date;
  }

  /**
   * Reads the store's calendar day and hour: from its configured time zone, else from the shop's UTC offset at
   * render time, else from the visitor's clock.
   * @returns {{ date: Date, hour: number }} The date sits at local midnight of the store's calendar day.
   */
  #storeNow() {
    const now = new Date();
    const timeZone = this.dataset.timeZone;

    if (timeZone) {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hourCycle: 'h23',
        }).formatToParts(now);
        /** @param {string} type */
        const part = (type) => Number(parts.find((entry) => entry.type === type)?.value);

        return { date: new Date(part('year'), part('month') - 1, part('day')), hour: part('hour') };
      } catch {
        // An unknown time zone falls through to the shop's offset
      }
    }

    const offset = parseUtcOffset(this.dataset.utcOffset);
    if (offset === null) {
      return { date: new Date(now.getFullYear(), now.getMonth(), now.getDate()), hour: now.getHours() };
    }

    const shifted = new Date(now.getTime() + offset * 60000);
    return {
      date: new Date(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate()),
      hour: shifted.getUTCHours(),
    };
  }

  /**
   * @param {string} value - A date as YYYY-MM-DD.
   * @returns {Date}
   */
  #parseDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
  }

  /**
   * @param {ProductSummaryUpdateEvent} event - The product summary update event.
   */
  #handleSummaryUpdate = (event) => {
    this.#total = event.detail.total;
    if (this.#line?.dataset.mode === 'dispatch') this.#renderDispatch();
  };

  /**
   * Re-renders the line for the selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected) return;

        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new line.
        if (detail.morphElementSelector) {
          // Another product is on the page, and its summary has not announced a total yet
          this.#total = null;
        } else {
          const newDelivery = detail.html.querySelector(`product-delivery[data-block-id="${this.dataset.blockId}"]`);
          if (!(newDelivery instanceof HTMLElement)) return;

          morph(this, newDelivery, MORPH_OPTIONS);
          // The morph leaves the element's own attributes alone
          this.hidden = newDelivery.hidden;
        }

        this.#render();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-delivery] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-delivery')) {
  customElements.define('product-delivery', ProductDelivery);
}
